import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  Avatar,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  Add,
  Edit,
  Delete,
  MoreVert,
  Business,
  Email,
  Phone,
  ContentCopy,
  PrecisionManufacturing,
} from '@mui/icons-material';
import {
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { useApp } from '../contexts/AppContext';
import { companyService, machineService, resellerService } from '../services/api';
import { LaserMachine, ResellerRecord } from '../types';
import CustomerFormDialog from '../components/CustomerFormDialog';
import PageHeader from '../components/ui/PageHeader';
import SurfaceCard from '../components/ui/SurfaceCard';
import FilterBar from '../components/ui/FilterBar';
import { copyTextToClipboard, promptManualCopy } from '../utils/clipboard';

interface CompanyRow {
  id: string;
  name: string;
  email: string;
  phone?: string;
  address?: string;
  vatNumber?: string;
}

const CustomersManagementPage: React.FC = () => {
  const { state } = useApp();
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [filteredCompanies, setFilteredCompanies] = useState<CompanyRow[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<CompanyRow | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Form dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<CompanyRow | null>(null);
  // Password display after creation
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [createdCredentials, setCreatedCredentials] = useState({ email: '', password: '', companyName: '' });
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignCompany, setAssignCompany] = useState<CompanyRow | null>(null);
  const [availableMachines, setAvailableMachines] = useState<LaserMachine[]>([]);
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[]>([]);

  // Unassigned companies state (admin only)
  const [unassignedCompanies, setUnassignedCompanies] = useState<any[]>([]);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [assignResellerDialog, setAssignResellerDialog] = useState(false);
  const [assignResellerCompany, setAssignResellerCompany] = useState<any>(null);
  const [resellers, setResellers] = useState<ResellerRecord[]>([]);
  const [selectedResellerId, setSelectedResellerId] = useState('');
  const [assignResellerSaving, setAssignResellerSaving] = useState(false);

  const currentUser = state.auth.user;
  const isAdmin = currentUser?.role === 'administrator';
  const isReseller = currentUser?.role === 'reseller';

  const loadCompanies = useCallback(async () => {
    try {
      const list = await companyService.getCompanies();
      setCompanies(list);
    } catch (error) {
      console.error('Error loading companies:', error);
      setNotification({ open: true, message: 'Errore nel caricamento delle aziende', severity: 'error' });
    }
  }, []);

  const loadUnassignedCompanies = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const list = await companyService.getUnassigned();
      setUnassignedCompanies(list);
    } catch (error) {
      console.error('Error loading unassigned companies:', error);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadCompanies();
    loadUnassignedCompanies();
  }, [loadCompanies, loadUnassignedCompanies]);

  const handleOpenAssignReseller = async (company: any) => {
    setAssignResellerCompany(company);
    setSelectedResellerId('');
    setAssignResellerDialog(true);
    try {
      const list = await resellerService.getResellers();
      setResellers(list);
    } catch {
      setNotification({ open: true, message: 'Errore caricamento resellers', severity: 'error' });
    }
  };

  const handleAssignReseller = async () => {
    if (!assignResellerCompany || !selectedResellerId) return;
    setAssignResellerSaving(true);
    try {
      await companyService.assignReseller(assignResellerCompany.id, selectedResellerId);
      setNotification({ open: true, message: 'Reseller assegnato con successo', severity: 'success' });
      setAssignResellerDialog(false);
      await loadCompanies();
      await loadUnassignedCompanies();
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || 'Errore assegnazione reseller';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setAssignResellerSaving(false);
    }
  };

  useEffect(() => {
    let filtered = companies;
    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredCompanies(filtered);
  }, [companies, searchTerm]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, company: CompanyRow) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompany(company);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAdd = () => {
    setEditingCompany(null);
    setDialogOpen(true);
  };

  const handleEdit = () => {
    if (selectedCompany) {
      setEditingCompany(selectedCompany);
      setDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialog(true);
    setAnchorEl(null);
  };

  const confirmDelete = async () => {
    if (selectedCompany) {
      try {
        await companyService.deleteCompany(selectedCompany.id);
        setNotification({ open: true, message: 'Azienda eliminata con successo', severity: 'success' });
        await loadCompanies();
      } catch (error: any) {
        const msg = error?.response?.data?.error?.message || 'Errore durante l\'eliminazione';
        setNotification({ open: true, message: msg, severity: 'error' });
      }
    }
    setDeleteDialog(false);
    setSelectedCompany(null);
  };

  const copyToClipboard = async (text: string) => {
    const copied = await copyTextToClipboard(text);
    if (copied) {
      setNotification({ open: true, message: 'Copiato negli appunti!', severity: 'success' });
      return;
    }

    promptManualCopy(text, 'il valore');
    setNotification({ open: true, message: 'Clipboard non disponibile: usa copia manuale', severity: 'error' });
  };

  const handleOpenAssignMachines = async () => {
    if (!selectedCompany) return;
    handleMenuClose();
    setAssignCompany(selectedCompany);
    setAssignDialogOpen(true);
    setAssignLoading(true);
    setSelectedMachineIds([]);
    try {
      const machineList = await machineService.getMachines('all');
      setAvailableMachines(machineList);
      const assigned = machineList
        .filter((m) => m.companyId === selectedCompany.id)
        .map((m) => m.id);
      setSelectedMachineIds(assigned);
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || 'Errore caricamento macchine';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setAssignLoading(false);
    }
  };

  const toggleMachineSelection = (machineId: string) => {
    setSelectedMachineIds((prev) =>
      prev.includes(machineId) ? prev.filter((id) => id !== machineId) : [...prev, machineId]
    );
  };

  const handleSaveAssignments = async () => {
    if (!assignCompany) return;
    setAssignSaving(true);
    try {
      const updates = availableMachines.flatMap((machine) => {
        const shouldBeAssigned = selectedMachineIds.includes(machine.id);
        const isAssignedToThisCompany = machine.companyId === assignCompany.id;

        if (shouldBeAssigned && !isAssignedToThisCompany) {
          return [machineService.updateMachine(machine.id, { companyId: assignCompany.id })];
        }
        if (!shouldBeAssigned && isAssignedToThisCompany) {
          return [machineService.updateMachine(machine.id, { companyId: null })];
        }
        return [];
      });

      await Promise.all(updates);
      setNotification({ open: true, message: 'Assegnazioni macchine aggiornate', severity: 'success' });
      setAssignDialogOpen(false);
    } catch (error: any) {
      const msg = error?.response?.data?.error?.message || 'Errore aggiornamento assegnazioni';
      setNotification({ open: true, message: msg, severity: 'error' });
    } finally {
      setAssignSaving(false);
    }
  };

  const canAdd = isAdmin || isReseller;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <PageHeader
          title="Clienti"
          subtitle="Anagrafiche delle aziende e accessi alla dashboard. Crea prima il cliente, poi assegna le licenze software."
          actions={
            canAdd ? (
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ minWidth: 160 }}>
                Nuovo cliente
              </Button>
            ) : null
          }
        />
      </Box>

      <Box sx={{ mb: 3 }}>
        <FilterBar>
          <TextField
            label="Cerca clienti"
            placeholder="Ragione sociale o email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><Search /></InputAdornment>
              ),
            }}
            sx={{ minWidth: 0, width: { xs: '100%', sm: 300 }, flexGrow: 1 }}
          />
        </FilterBar>
      </Box>

      {/* Unassigned Companies Section (Admin Only) */}
      {isAdmin && unassignedCompanies.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <SurfaceCard>
            <Box
              sx={{
                px: 2.5,
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                borderBottom: showUnassigned ? '1px solid rgba(168, 191, 255, 0.14)' : 'none',
              }}
              onClick={() => setShowUnassigned(!showUnassigned)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PersonAdd sx={{ color: '#FFB454' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Aziende in attesa di assegnazione
                </Typography>
                <Chip label={unassignedCompanies.length} size="small" color="warning" />
              </Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {showUnassigned ? 'Nascondi' : 'Mostra'}
              </Typography>
            </Box>
            <Collapse in={showUnassigned}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Azienda</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Utente</TableCell>
                      <TableCell>Data Registrazione</TableCell>
                      <TableCell>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unassignedCompanies.map((company) => (
                      <TableRow key={company.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {company.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{company.email}</Typography>
                        </TableCell>
                        <TableCell>
                          {company.users?.map((u: any) => (
                            <Typography key={u.id} variant="body2">
                              {u.displayName} ({u.email})
                            </Typography>
                          ))}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(company.createdAt).toLocaleDateString('it-IT')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleOpenAssignReseller(company)}
                          >
                            Assegna Reseller
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </SurfaceCard>
        </Box>
      )}

      <Box sx={{ mt: 4 }}>
        <SurfaceCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Azienda</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Telefono</TableCell>
                  <TableCell>P.IVA</TableCell>
                  <TableCell>Azioni</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCompanies.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="h6" color="text.secondary">
                        {searchTerm ? 'Nessuna azienda trovata' : 'Nessuna azienda registrata'}
                      </Typography>
                      {canAdd && (
                        <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ mt: 2 }}>
                          Aggiungi Prima Azienda
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCompanies.map((company) => (
                    <TableRow key={company.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <Business />
                          </Avatar>
                          <Typography variant="body2" fontWeight="bold">{company.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{company.email}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">{company.phone || '-'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{company.vatNumber || '-'}</TableCell>
                      <TableCell>
                        <IconButton onClick={(e) => handleMenuClick(e, company)} size="small">
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </SurfaceCard>
      </Box>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleEdit}>
          <Edit sx={{ mr: 1 }} /> Modifica
        </MenuItem>
        <MenuItem onClick={handleOpenAssignMachines}>
          <PrecisionManufacturing sx={{ mr: 1 }} /> Assegna Macchine
        </MenuItem>
        {(isAdmin || isReseller) && (
          <MenuItem onClick={handleDelete}>
            <Delete sx={{ mr: 1 }} /> Elimina
          </MenuItem>
        )}
      </Menu>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare l'azienda "{selectedCompany?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Annulla</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Elimina</Button>
        </DialogActions>
      </Dialog>

      <CustomerFormDialog
        open={dialogOpen} company={editingCompany}
        onClose={() => { setDialogOpen(false); void loadCompanies(); void loadUnassignedCompanies(); }}
        onSaved={() => {
          setDialogOpen(false);
          setNotification({ open: true, message: editingCompany ? 'Cliente aggiornato' : 'Cliente creato', severity: 'success' });
          void loadCompanies(); void loadUnassignedCompanies();
        }}
        onCredentials={credentials => { setCreatedCredentials(credentials); setPasswordDialog(true); }}
      />

      {/* Password Display Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Accesso cliente pronto</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Salva queste credenziali ora! La password non potrà essere recuperata in futuro.
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2"><strong>Azienda:</strong> {createdCredentials.companyName}</Typography>
            <TextField
              label="Email (login)"
              value={createdCredentials.email}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copyToClipboard(createdCredentials.email)}>
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Password"
              value={createdCredentials.password}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => copyToClipboard(createdCredentials.password)}>
                      <ContentCopy />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)} variant="contained">Ho salvato le credenziali</Button>
        </DialogActions>
      </Dialog>

      {/* Machine Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assegna Macchine a {assignCompany?.name}</DialogTitle>
        <DialogContent>
          {assignLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Seleziona le macchine da associare a questo cliente. Deseleziona per rimuovere l&apos;associazione.
              </Alert>
              <List sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {availableMachines.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="Nessuna macchina disponibile" />
                  </ListItem>
                ) : (
                  availableMachines.map((machine) => (
                    <ListItem key={machine.id}>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedMachineIds.includes(machine.id)}
                          onChange={() => toggleMachineSelection(machine.id)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={machine.name}
                        secondary={`${machine.model} • S/N ${machine.serialNumber}`}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assignSaving}>
            Annulla
          </Button>
          <Button onClick={handleSaveAssignments} variant="contained" disabled={assignSaving || assignLoading}>
            {assignSaving ? 'Salvataggio...' : 'Salva Assegnazioni'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign Reseller Dialog */}
      <Dialog open={assignResellerDialog} onClose={() => setAssignResellerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assegna Reseller a {assignResellerCompany?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Seleziona il reseller a cui assegnare questa azienda auto-registrata.
          </Typography>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Reseller</InputLabel>
            <Select
              value={selectedResellerId}
              label="Reseller"
              onChange={(e: SelectChangeEvent) => setSelectedResellerId(e.target.value)}
            >
              {resellers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignResellerDialog(false)} disabled={assignResellerSaving}>
            Annulla
          </Button>
          <Button
            onClick={handleAssignReseller}
            variant="contained"
            disabled={!selectedResellerId || assignResellerSaving}
          >
            {assignResellerSaving ? 'Salvataggio...' : 'Assegna'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomersManagementPage;
